<template>
  <!-- Teleport na body, z-50 zamiast wartości dowolnych: Tailwind 2 bez JIT nie generuje
       klas typu z-[100] czy max-h-[92vh] — takie klasy nie tworzą ŻADNEGO CSS. -->
  <Teleport to="body">
  <div class="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-labelledby="subiekt-doc-title">
    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="close()"></div>

    <div class="relative flex subiekt-doc-panel w-full max-w-7xl flex-col overflow-hidden rounded-t-xl bg-white shadow-xl sm:rounded-xl">

      <div class="flex items-start gap-4 border-b border-gray-200 px-6 py-5">
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-center gap-2">
            <h3 id="subiekt-doc-title" class="text-base font-semibold text-gray-900">{{ document.tytul }}</h3>
            <span class="inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              {{ document.action }}
            </span>
          </div>
          <!-- Na dokumencie porównawczym zdanie o wysyłce było po prostu nieprawdziwe. -->
          <p v-if="document.tylkoPorownanie" class="mt-1 text-sm text-gray-500">
            Zestawienie do porównania z fakturą — w Subiekcie dokument powstaje sam,
            jako skutek magazynowy faktury. Stąd nic nie wychodzi.
          </p>
          <p v-else class="mt-1 text-sm text-gray-500">
            To poleci do Subiekta jako jeden dokument. Sprawdź pozycje przed wysłaniem.
          </p>
        </div>
        <button @click="close()" type="button" class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-gray-400 transition duration-200 hover:bg-gray-100 hover:text-gray-700">
          <span class="sr-only">Zamknij</span>
          <XIcon class="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto px-6 py-5">

        <!-- Nagłówek dokumentu: dokładnie te pola, które lecą w żądaniu do mostu. -->
        <h4 class="text-xs font-medium uppercase tracking-wide text-gray-500">Nagłówek dokumentu</h4>
        <dl class="mt-2 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
          <div v-for="field in headerFields" :key="field.label"
            :class="[field.dlugie ? 'sm:col-span-2 lg:col-span-3 block' : 'flex items-center justify-between gap-3',
                     'border-b border-gray-100 py-1.5']">
            <dt :class="[field.dlugie ? 'mb-1' : '', 'flex items-center gap-2 text-sm text-gray-500']">
              <component :is="field.ikona" class="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
              {{ field.label }}
            </dt>
            <!-- Długie pola dostają całą szerokość i wyrównanie do lewej. Powód jest
                 mierzalny: w siatce wysokość rzędu wyznacza WYŻSZA komórka, więc trzy
                 wiersze „Uwag" rozciągały sąsiedni „Numer oryginalny" z 37 do 77 px.
                 Linia oddzielająca odjeżdżała 40 px pod tekst i pole traciło z nią
                 związek — stąd wrażenie, że wszystko się zlewa. Przy okazji zawijany
                 tekst wyrównany do prawej ma poszarpaną lewą krawędź i czyta się gorzej. -->
            <dd v-if="field.plakietka && field.value != null && field.value !== ''"
              :class="[field.plakietka, 'inline-flex items-center rounded-md px-2 py-0.5 text-sm font-medium']">
              {{ field.value }}
            </dd>
            <dd v-else :class="[field.dlugie ? 'text-left' : 'text-right',
                         'text-sm font-medium text-gray-900 break-words']">{{ field.value || '—' }}</dd>
          </div>
        </dl>

        <h4 class="mt-6 text-xs font-medium uppercase tracking-wide text-gray-500">
          Pozycje ({{ document.towary.length }})
        </h4>
        <div class="mt-2 overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-gray-200">
              <th class="py-1.5 pr-3 text-left font-medium text-gray-500">Symbol</th>
              <th class="py-1.5 pr-3 text-left font-medium text-gray-500">Nazwa</th>
              <th v-if="pokazOpis" class="py-1.5 pr-3 text-left font-medium text-gray-500">Opis</th>
              <th v-if="pokazZamowienie" class="py-1.5 pr-3 text-left font-medium text-gray-500">Zamówienie</th>
              <th class="py-1.5 text-right font-medium text-gray-500">Ilość</th>
            </tr>
          </thead>
          <tbody>
            <!-- Wariant + zamówienie: ten sam mebel wystąpi w dokumencie tyle razy,
                 ilu jest klientów, więc sam symbol dawałby powtórzone klucze. -->
            <tr v-for="item in sortedItems" :key="rowKey(item)" class="border-b border-gray-100">
              <td class="py-1.5 pr-3 align-top whitespace-nowrap">
                <span v-if="item.brakSymbolu"
                  class="rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800">brak symbolu</span>
                <span v-else class="text-gray-500">{{ item.symbolTowaru }}</span>
              </td>
              <td class="py-1.5 pr-3 align-top text-gray-900">
                {{ item.nazwaProduktu || item.nazwaTowaru }}
                <span v-if="item.nazwaWariantu"
                  class="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">{{ item.nazwaWariantu }}</span>
              </td>
              <td v-if="pokazOpis" class="py-1.5 pr-3 align-top text-gray-600">
                <span v-if="item.opis">{{ item.opis }}</span>
                <span v-else class="text-gray-400">—</span>
              </td>
              <td v-if="pokazZamowienie" class="py-1.5 pr-3 align-top">
                <p v-if="item.trasa" class="text-gray-600">{{ item.trasa }}</p>
                <p v-if="item.kanalSurowy && item.kanalSurowy.length" class="mt-0.5 text-xs text-gray-400">
                  {{ item.kanalSurowy.join(' · ') }}
                </p>
                <span v-if="!item.trasa" class="text-gray-300">—</span>
              </td>
              <td class="py-1.5 text-right align-top text-gray-700 whitespace-nowrap">{{ item.iloscJm }} szt</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td :colspan="2 + (pokazOpis ? 1 : 0) + (pokazZamowienie ? 1 : 0)"
                class="py-2 text-sm font-medium text-gray-900">Razem</td>
              <td class="py-2 text-right text-sm font-medium text-gray-900 whitespace-nowrap">{{ totalQuantity }} szt</td>
            </tr>
          </tfoot>
        </table>
        </div>


        <p v-if="document.blad" class="mt-6 rounded-md bg-red-50 px-4 py-3 text-sm text-red-800">
          {{ document.blad }}
        </p>
      </div>

      <div class="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row-reverse sm:items-center">
        <button
          v-if="canSend"
          @click="$emit('send', document)"
          :disabled="!sendingEnabled || sending"
          type="button"
          :class="[(!sendingEnabled || sending) ? 'cursor-not-allowed opacity-50' : 'hover:bg-indigo-500 active:scale-95', 'inline-flex subiekt-doc-touch items-center justify-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200']"
        >
          <RefreshIcon v-if="sending" class="h-4 w-4 animate-spin" aria-hidden="true" />
          {{ sending ? 'Wysyłam…' : 'Wyślij do Subiekta' }}
        </button>
        <button @click="$emit('goto', document)" type="button" class="inline-flex subiekt-doc-touch items-center justify-center gap-x-1.5 rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 transition-all duration-200 hover:bg-gray-50">
          Przejdź do dokumentu
          <ArrowNarrowRightIcon class="h-4 w-4 text-gray-400" aria-hidden="true" />
        </button>
        <button @click="close()" type="button" class="inline-flex subiekt-doc-touch items-center justify-center rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 transition-all duration-200 hover:bg-gray-50">
          Zamknij
        </button>
        <p v-if="canSend && !sendingEnabled" class="flex-1 text-xs text-gray-500 sm:text-left">
          Wysyłka wyłączona w Configs/subiekt.enabled.
        </p>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script>
import { XIcon, RefreshIcon, ArrowNarrowRightIcon,
  DocumentTextIcon, ArchiveIcon, OfficeBuildingIcon, CalendarIcon, HashtagIcon,
  AnnotationIcon, MenuAlt2Icon } from '@heroicons/vue/solid';

export default {
  name: 'SubiektDocumentModal',
  components: { XIcon, RefreshIcon, ArrowNarrowRightIcon,
    DocumentTextIcon, ArchiveIcon, OfficeBuildingIcon, CalendarIcon, HashtagIcon,
    AnnotationIcon, MenuAlt2Icon },
  props: {
    document: { type: Object, required: true },
    sendingEnabled: { type: Boolean, default: false },
    sending: { type: Boolean, default: false },
    // Mapa numer -> nazwa magazynu, przekazywana z listy dokumentów.
    warehouses: { type: Object, default: () => ({}) },
  },
  emits: ['close', 'send', 'goto'],
  computed: {
    // Pozycje wymagajace uwagi ida na GORE listy. Przy dokumencie na 112 pozycji
    // jedna bez symbolu potrafila siedziec w polowie tabeli i trzeba jej bylo szukac
    // wzrokiem, mimo ze to ona blokuje wysylke calego dokumentu.
    //
    // Sortujemy KOPIE. Kolejnosc w document.towary jest kolejnoscia pozycji na
    // dokumencie w Subiekcie - przestawienie jej w miejscu zmienialoby to, co poleci.
    // Wiersze to te same obiekty, wiec v-model przy ilosciach i cenach dziala dalej.
    sortedItems() {
      const items = Array.isArray(this.document?.towary) ? this.document.towary : [];
      // 0 = brak symbolu (blokuje wysylke), 1 = pominieta, 2 = reszta.
      const waga = (item) => {
        if(item?.brakSymbolu) return 0;
        if(item?.pominieta) return 1;
        return 2;
      };
      // Sortowanie po indeksie jako drugim kluczu zachowuje pierwotna kolejnosc
      // wewnatrz kazdej grupy - inaczej pozycje skakalyby przy kazdym renderze.
      return items
        .map((item, index) => ({ item, index }))
        .sort((a, b) => waga(a.item) - waga(b.item) || a.index - b.index)
        .map(wpis => wpis.item);
    },
    totalQuantity() {
      return (this.document.towary || []).reduce((sum, item) => sum + Number(item.iloscJm || 0), 0);
    },
    // Kolumna z danymi klienta ma sens tylko na wydaniach — na produkcji byłaby pusta.
    pokazZamowienie() {
      return (this.document.towary || []).some(item => item.orderId || item.trasa);
    },
    pokazOpis() {
      return (this.document.towary || []).some(item => item.opis);
    },
    canSend() {
      // Dokument porównawczy nie ma dokąd polecieć, a dokument z blokadami i tak
      // odbije się o backend — przycisk pokazywał się na obu i kłamał.
      if(this.document.tylkoPorownanie) return false;
      if((this.document.blokady || []).length > 0) return false;
      return this.document.status === 'SZKIC'
        || this.document.status === 'DO_WYSTAWIENIA'
        || this.document.status === 'BLAD';
    },
    headerFields() {
      const doc = this.document;
      return [
        { label: 'Typ dokumentu', value: doc.action, ikona: 'DocumentTextIcon',
          plakietka: this.kolorTypu(doc.action) },
        { label: 'Magazyn', value: this.warehouseName(doc.werehouseId), ikona: 'ArchiveIcon',
          plakietka: 'bg-gray-100 text-gray-700' },
        { label: 'Kontrahent', value: doc.symbolKontrahenta, ikona: 'OfficeBuildingIcon',
          plakietka: 'bg-indigo-50 text-indigo-700' },
        { label: 'Data dokumentu', value: doc.dzien, ikona: 'CalendarIcon' },
        { label: 'Numer oryginalny', value: doc.numerOryginalny, ikona: 'HashtagIcon' },
        // Trasy, z których złożono wydanie — w Subiekcie ląduje to w polu „Uwagi".
        // Oba pola bywają na kilka wierszy, więc idą na pełną szerokość, pod etykietą.
        { label: 'Uwagi', value: doc.uwagi, dlugie: true, ikona: 'AnnotationIcon' },
        { label: 'Opis', value: doc.opis, dlugie: true, ikona: 'MenuAlt2Icon' },
      ];
    },
  },
  methods: {
    // Ten sam klucz co w backendzie i w widoku szczegółów: wariant + zamówienie.
    kolorTypu(action) {
      if(action === 'WZ') return 'bg-blue-50 text-blue-700';
      if(action === 'PZ') return 'bg-green-50 text-green-700';
      return 'bg-gray-100 text-gray-700';
    },
    warehouseName(docNumber) {
      if(docNumber == null || docNumber === '') return '—';
      const labelText = (this.warehouses || {})[String(docNumber)];
      return labelText ? `${docNumber} · ${labelText}` : String(docNumber);
    },
    rowKey(item) {
      const wariant = item.productId && item.variantId
        ? `${item.productId}|${item.variantId}`
        : (item.symbolTowaru || item.nazwaTowaru || '');
      return item.orderId ? `${wariant}|${item.orderId}` : wariant;
    },
    close() {
      if(this.sending) return;
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
/* Tailwind 2 (postcss7-compat, bez JIT) nie generuje klas z wartościami dowolnymi,
   np. max-h-[92vh] czy min-h-[44px] — takie klasy nie tworzą ŻADNEGO CSS.
   Konkretne wymiary trzymamy więc tutaj. */
.subiekt-doc-panel {
  max-height: 92vh;
}
.subiekt-doc-touch {
  min-height: 44px;
}
</style>
